import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Home, 
  Users, 
  BookOpen, 
  Menu,
  Flame,
  UserCircle,
  CalendarDays,
  Shield,
  LogOut,
  ChevronDown,
  Building2,
  Tags
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FRONTEND_VERSION } from '@/lib/api';
import { NotificationDropdown } from '@/components/NotificationDropdown';

const NavLink = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors",
        isActive 
          ? "bg-primary text-primary-foreground" 
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <Icon className="h-5 w-5" />
      <span>{item.name}</span>
    </Link>
  );
};

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 px-2">
    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary text-primary-foreground">
      <Flame className="h-6 w-6" />
    </div>
    <div className="flex flex-col">
      <span className="font-heading text-xl font-semibold text-foreground">Forjadores</span>
      <span className="text-xs text-muted-foreground">Comunidade de Fé</span>
    </div>
  </Link>
);

const IgrejaSeletor = () => {
  const { igrejas, igrejaAtual, selectIgreja, loadingIgrejas } = useApp();
  
  if (loadingIgrejas || igrejas.length === 0) {
    return null;
  }
  
  return (
    <div className="px-4 py-3 border-t border-border">
      <label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1">
        <Building2 className="w-3 h-3" />
        Igreja Ativa
      </label>
      <Select
        value={igrejaAtual?.id || ''}
        onValueChange={(value) => {
          const igreja = igrejas.find(i => i.id === value);
          if (igreja) selectIgreja(igreja);
        }}
      >
        <SelectTrigger className="w-full h-9 text-sm">
          <SelectValue placeholder="Selecione uma igreja" />
        </SelectTrigger>
        <SelectContent>
          {igrejas.map(igreja => (
            <SelectItem key={igreja.id} value={igreja.id}>
              {igreja.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const UserMenu = ({ user, isAdminMaster, onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-full justify-start gap-3 px-3 py-6">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary">
              {user?.nome?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className="text-sm font-medium truncate w-full text-left">
              {user?.nome || 'Usuário'}
            </span>
            <span className="text-xs text-muted-foreground truncate w-full text-left">
              {user?.email || ''}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isAdminMaster && (
          <>
            <DropdownMenuItem onClick={() => navigate('/admin')}>
              <Shield className="mr-2 h-4 w-4" />
              Dashboard Admin
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Sidebar = ({ className, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdminMaster, logout } = useAuth();

  const navigation = [
    { name: 'Início', href: '/', icon: Home },
    { name: 'Dashboard Igreja', href: '/igreja-dashboard', icon: Building2 },
    { name: 'Células', href: '/celulas', icon: Users },
    { name: 'Categorias', href: '/categorias', icon: Tags },
    { name: 'Membros', href: '/membros', icon: UserCircle },
    { name: 'Encontros', href: '/encontros', icon: BookOpen },
    { name: 'Calendário', href: '/calendario', icon: CalendarDays },
  ];

  // Add admin link if user is admin master
  if (isAdminMaster) {
    navigation.push({ name: 'Admin Dashboard', href: '/admin', icon: Shield });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={cn("flex flex-col h-full bg-card border-r border-border", className)}>
      <div className="p-6">
        <Logo />
      </div>
      
      {/* Igreja Selector */}
      <IgrejaSeletor />
      
      <ScrollArea className="flex-1 px-4 pt-4">
        <nav className="flex flex-col gap-1">
          {navigation.map((item) => (
            <NavLink 
              key={item.href} 
              item={item} 
              isActive={location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href))}
              onClick={onClose}
            />
          ))}
        </nav>
      </ScrollArea>
      
      {/* User Menu */}
      <div className="p-4 border-t border-border">
        <UserMenu user={user} isAdminMaster={isAdminMaster} onLogout={handleLogout} />
      </div>
      
      <div className="px-4 pb-4">
        <div className="p-4 rounded-xl bg-muted/50">
          <p className="text-sm text-muted-foreground text-center">
            "Onde dois ou três estiverem reunidos em meu nome, ali estou no meio deles."
          </p>
          <p className="text-xs text-muted-foreground text-center mt-2 font-medium">
            Mateus 18:20
          </p>
        </div>
        <p className="text-xs text-muted-foreground/60 text-center mt-3">
          v{FRONTEND_VERSION}
        </p>
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <Sidebar />
      </aside>

      {/* Desktop Header with Notifications */}
      <header className="hidden lg:flex fixed top-0 right-0 left-72 z-40 h-16 bg-card/80 backdrop-blur-md border-b border-border items-center justify-end px-6">
        <NotificationDropdown />
      </header>

      {/* Mobile Header */}
      <header className="sticky top-0 z-40 lg:hidden bg-card/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo />
          
          <div className="flex items-center gap-2">
            <NotificationDropdown />
            
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Abrir menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
                <Sidebar onClose={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-72 lg:pt-16">
        <div className="min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
};
